import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';

import AppDialog from '../../../shared/components/AppDialog';
import { getApiErrorMessage } from '../../../shared/api/errors';
import { useAuth } from '../contexts/AuthenticationContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useProfile, useUpdateProfile } from '../hooks';
import type { UserProfile } from '../endpoints';

// Pages that already ask for these details themselves.
const PROFILE_PAGES = ['/profile', '/complete-profile'];

const blank = (value: string | null | undefined) => !value || !value.trim();

/** Company is asked only of learners with no organization in HRA, which names it. */
const needsCompany = (profile: UserProfile) => blank(profile.organization_name);

/** A learner whose name, job title or company HRA doesn't have yet. */
export function missingProfileDetails(profile: UserProfile | undefined): boolean {
  return !!profile && profile.role === 'user' && (
    blank(profile.first_name) || blank(profile.last_name) || blank(profile.title)
    || (needsCompany(profile) && blank(profile.company_name))
  );
}

/**
 * Requires a learner's name and job title: right after sign-up, and on the next
 * visit of anyone whose account lacks them, on whatever page they land. It
 * can't be closed until they're saved (Sue Thompson, 8 Oct 2026). Accounts
 * often start with only an email (a manager registered them, or they signed up
 * with just that), and those blanks reached WZ's HubSpot as contacts with no
 * name or title. Company too, for anyone not in an organization: without it
 * WZ can't put the contact under a company in HubSpot (WPDS-113).
 */
export default function ProfileDetailsDialog() {
  const { data: profile } = useProfile();
  const { target: impersonationTarget } = useImpersonation();
  const { pathname } = useLocation();
  const updateProfile = useUpdateProfile();
  const { updateUser } = useAuth();
  const [form, setForm] = useState({ first_name: '', last_name: '', title: '', company_name: '' });

  useEffect(() => {
    if (!profile) return;
    setForm({
      first_name: profile.first_name ?? '',
      last_name: profile.last_name ?? '',
      title: profile.title ?? '',
      company_name: profile.company_name ?? '',
    });
  }, [profile]);

  // Never while an admin is viewing as someone: they would be filling in the
  // learner's profile for them.
  const open = !PROFILE_PAGES.includes(pathname.replace(/\/$/, '')) && !impersonationTarget && missingProfileDetails(profile);
  const companyRequired = !!profile && needsCompany(profile);
  const canSave = !blank(form.first_name) && !blank(form.last_name) && !blank(form.title)
    && !(companyRequired && blank(form.company_name));

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const updated = await updateProfile.mutateAsync({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      title: form.title.trim(),
      company_name: form.company_name.trim(),
    });
    updateUser({ first_name: updated.first_name ?? undefined, last_name: updated.last_name ?? undefined, name: updated.full_name });
  };

  return (
    <AppDialog
      open={open}
      // Above anything else that opens on arrival, e.g. the course popup an
      // On-Demand Enroll link opens: the details come first.
      onTop
      title="Complete your profile"
      actions={
        <Button type="submit" form="profile-details-form" variant="contained" disabled={!canSave || updateProfile.isPending}>
          {updateProfile.isPending ? 'Saving…' : 'Save'}
        </Button>
      }
    >
      <Box component="form" id="profile-details-form" onSubmit={save} sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
          Tell us who you are, so your certificates and records carry your name and role.
        </Typography>
        {updateProfile.isError && <Alert severity="error">{getApiErrorMessage(updateProfile.error)}</Alert>}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <TextField fullWidth label="First name" value={form.first_name} onChange={set('first_name')} required autoFocus />
          <TextField fullWidth label="Last name" value={form.last_name} onChange={set('last_name')} required />
        </Box>
        <TextField fullWidth label="Job title" value={form.title} onChange={set('title')} required />
        <TextField fullWidth label="Company" value={form.company_name} onChange={set('company_name')} required={companyRequired}
          helperText={companyRequired ? 'Where you work, or the agency you work for.' : undefined} />
      </Box>
    </AppDialog>
  );
}
